/**
 * Pure helpers behind the review viewer's collapsible sections and
 * changed-line markers.
 *
 * Sections are computed from the SOURCE lines (fence-aware ATX-heading scan),
 * never from the rendered tree — react-markdown's `node.position` refers to
 * the same 1-based source lines, so a block's membership in a section is a
 * plain range check and the full `contentMd` still renders untouched (no
 * slicing, anchors survive).
 */
import type { AdminReviewComment, ReviewDiff } from "../api/admin";

export interface ReviewSection {
  /** `s:<headingLine>` — stable across renders, unique per document. */
  id: string;
  title: string;
  /** 1-based source line of the section's heading (== startLine). */
  headingLine: number;
  startLine: number;
  /** Last line before the next section heading (or the last line). */
  endLine: number;
}

const FENCE_RE = /^(\s{0,3})(`{3,}|~{3,})/;

/**
 * Split a markdown source into fold sections.
 *
 * H2 (`## `) boundaries; a document without H2s falls back to H1; without
 * either the result is `[]` and the page behaves exactly as today. Headings
 * inside fenced code blocks don't count. Lines before the first heading form
 * no section — the preamble never folds.
 */
export function parseSections(contentMd: string): ReviewSection[] {
  const lines = contentMd.split("\n");

  const headingsAt = (marker: RegExp): Array<{ line: number; title: string }> => {
    const out: Array<{ line: number; title: string }> = [];
    let fence: string | null = null;
    for (let i = 0; i < lines.length; i++) {
      const text = lines[i];
      const fenceMatch = FENCE_RE.exec(text);
      if (fenceMatch) {
        const mark = fenceMatch[2][0];
        if (fence === null) fence = mark;
        else if (mark === fence) fence = null;
        continue;
      }
      if (fence !== null) continue;
      const m = marker.exec(text);
      if (m) out.push({ line: i + 1, title: m[1].trim() });
    }
    return out;
  };

  let headings = headingsAt(/^##\s+(.*)$/);
  if (headings.length === 0) headings = headingsAt(/^#\s+(.*)$/);
  if (headings.length === 0) return [];

  return headings.map((h, i) => ({
    id: `s:${h.line}`,
    title: h.title,
    headingLine: h.line,
    startLine: h.line,
    endLine: i + 1 < headings.length ? headings[i + 1].line - 1 : lines.length,
  }));
}

/** The section containing a 1-based source line, if any (binary search). */
export function sectionForLine(
  sections: ReviewSection[],
  line: number,
): ReviewSection | undefined {
  let lo = 0;
  let hi = sections.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const s = sections[mid];
    if (line < s.startLine) hi = mid - 1;
    else if (line > s.endLine) lo = mid + 1;
    else return s;
  }
  return undefined;
}

/**
 * New-side line numbers touched by a diff. Added lines mark themselves; a
 * pure deletion marks the new-side line it sits at, so a removed passage
 * still flags its neighbourhood (and its section).
 */
export function changedNewLines(diff: ReviewDiff): Set<number> {
  const out = new Set<number>();
  for (const hunk of diff.hunks) {
    let newLine = hunk.to_start;
    for (const ln of hunk.lines) {
      if (ln.op === "add") {
        out.add(newLine);
        newLine++;
      } else if (ln.op === "ctx") {
        newLine++;
      } else {
        // del: consumes no new-side line — mark where the removal sits.
        out.add(Math.max(1, newLine));
      }
    }
  }
  return out;
}

/**
 * The smart fold default: a section starts OPEN iff it carries a comment
 * anchor or (once diff data exists) a changed line; everything else starts
 * collapsed. A document with ≤1 section never folds — that would be noise.
 */
export function defaultCollapsedIds(
  sections: ReviewSection[],
  commentLines: Set<number>,
  changedLines: Set<number> | null,
): Set<string> {
  if (sections.length <= 1) return new Set();
  const inRange = (s: ReviewSection, lines: Set<number>): boolean => {
    for (const line of lines) {
      if (line >= s.startLine && line <= s.endLine) return true;
    }
    return false;
  };
  const collapsed = new Set<string>();
  for (const s of sections) {
    const open =
      inRange(s, commentLines) || (changedLines !== null && inRange(s, changedLines));
    if (!open) collapsed.add(s.id);
  }
  return collapsed;
}

/** Current-version comment count per section id (stale anchors excluded). */
export function commentCountBySection(
  sections: ReviewSection[],
  comments: AdminReviewComment[],
  docVersion: number,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const c of comments) {
    const stale = c.stale ?? c.content_version < docVersion;
    if (stale) continue;
    const s = sectionForLine(sections, c.line_start);
    if (s) counts.set(s.id, (counts.get(s.id) ?? 0) + 1);
  }
  return counts;
}
